import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ArrowUpRight, HelpCircle, Sparkles, Wand2, Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";

/**
 * EnrichedSuggestion - a follow-up suggestion chip carrying metadata, richer
 * than the plain-string chips in `Suggestions`. Renderer for the K2
 * `enriched_suggestion` block, one of the post-answer signals the runtime's
 * response post-processor emits (ais-agent-runtime `PostProcessSignals`,
 * AISquare-Studio/ais-agent-runtime#115). The platform's chat `BlockRenderer`
 * passes an `enriched_suggestion` block's `props` straight through, so the prop
 * names match the block payload exactly. INTERACTIVE.
 *
 * Where `Suggestions` renders bare quick-replies, an enriched suggestion knows
 * what KIND of follow-up it is (a clarifying question, a richer rewrite, an
 * experience to create) and optionally how CONFIDENT the model is, so the chip
 * can carry a kind icon and a subtle confidence tint. The runtime emits these
 * one at a time; the BlockRenderer adapter accepts either a single suggestion
 * or a `suggestions: [...]` list (a thin wrapper renders the row).
 *
 * Block props (`{ label, kind?, confidence?, value?, submit_to, receipt? }`,
 * or `{ suggestions: [{ label, kind?, confidence?, value? }], prompt?,
 * submit_to, receipt? }` for the list form):
 *   - `label` - the chip text. Required.
 *   - `kind?` - the follow-up class: `"question"` | `"rewrite"` |
 *     `"experience"` | `"explore"` | a free string. Drives the leading icon.
 *   - `confidence?` - 0..1 model confidence; tints the chip (subtle at low
 *     confidence, fuller at high). Out-of-range / absent reads as neutral.
 *   - `value?` - what gets sent back as the next turn. Defaults to `label`.
 *   - `submit_to` - destination routing token (mirrors `form.submit_to`).
 *   - `receipt?` - once chosen, the durable settled state. When present the
 *     settled receipt renders instead of the live chip(s).
 *
 * On click the block emits the shared block-submit event keyed by `block_id` +
 * the chosen suggestion's id (the `kind` when present, else the label) + value,
 * via `blockSubmit.tsx` - the same transport the other interactive blocks use.
 *
 * Client-only props (never part of the streamed block): `className`, `onSelect`
 * (wired by the block adapter to the submit context), `disabled`.
 *
 * Token-themed (`bg-card`, `border-border`, `hover:border-accent`,
 * `bg-accent/10`, ...) with no hardcoded colors. SSR-safe.
 */

/** Known follow-up kinds. Open union - an unknown string reads as neutral. */
export type EnrichedSuggestionKind =
  | "question"
  | "rewrite"
  | "experience"
  | "explore"
  | (string & {});

/** One enriched follow-up suggestion. */
export interface EnrichedSuggestionItem {
  /** Chip text. Required. */
  label: string;
  /** Follow-up class; drives the leading icon. */
  kind?: EnrichedSuggestionKind;
  /** 0..1 model confidence; tints the chip. */
  confidence?: number;
  /** Value sent back as the next turn. Defaults to `label`. */
  value?: string;
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  question: HelpCircle,
  rewrite: Wand2,
  experience: Sparkles,
  explore: Search,
};

/** Pick the leading icon for a kind, defaulting to a "follow-up" arrow. */
function iconForKind(kind: string | undefined): React.ComponentType<{ className?: string }> {
  return (kind && ICONS[kind]) || ArrowUpRight;
}

/**
 * Three confidence tiers, so the tint is a stable bucket rather than a
 * continuous (and visually noisy) value. Absent / out-of-range -> neutral.
 */
function confidenceTier(confidence: number | undefined): "low" | "medium" | "high" | undefined {
  if (typeof confidence !== "number" || Number.isNaN(confidence)) return undefined;
  if (confidence < 0 || confidence > 1) return undefined;
  if (confidence >= 0.75) return "high";
  if (confidence >= 0.4) return "medium";
  return "low";
}

const chipVariants = cva(
  cn(
    "group inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-50",
    // Slide-then-fade, matching the plain Suggestions chips; disabled under
    // reduced motion.
    "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both",
  ),
  {
    variants: {
      tier: {
        none: "border-border bg-card text-foreground hover:border-accent hover:bg-accent/10 hover:text-accent",
        low: "border-border bg-card text-muted-foreground hover:border-accent hover:bg-accent/10 hover:text-accent",
        medium:
          "border-accent/30 bg-accent/5 text-foreground hover:border-accent hover:bg-accent/10 hover:text-accent",
        high: "border-accent/40 bg-accent/10 text-accent hover:bg-accent/15",
      },
    },
    defaultVariants: { tier: "none" },
  },
);

const enrichedSuggestionVariants = cva("not-prose flex flex-col gap-2");

export interface EnrichedSuggestionProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect">,
    VariantProps<typeof enrichedSuggestionVariants> {
  /** Optional lead-in line above the chips. */
  prompt?: string;
  /** The enriched suggestions. Required (pass a single-item array for one chip). */
  suggestions: EnrichedSuggestionItem[];
  /** Settled state - renders the receipt instead of live chips when present. */
  receipt?: BlockReceipt | unknown;
  /** Selection handler. Wired by the block adapter to the submit context. */
  onSelect?: (suggestion: EnrichedSuggestionItem) => void;
  /** Disable interaction (e.g. while the next turn is in flight). */
  disabled?: boolean;
}

export const EnrichedSuggestion = React.forwardRef<HTMLDivElement, EnrichedSuggestionProps>(
  function EnrichedSuggestion(
    { className, prompt, suggestions, receipt, onSelect, disabled = false, ...rest },
    ref,
  ) {
    const settled = readReceipt(receipt);

    if (settled) {
      return (
        <div
          ref={ref}
          className={cn(enrichedSuggestionVariants(), className)}
          data-slot="enriched-suggestion"
          data-settled="true"
          {...rest}
        >
          <BlockReceiptView receipt={settled} />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(enrichedSuggestionVariants(), className)}
        data-slot="enriched-suggestion"
        {...rest}
      >
        {prompt ? <p className="text-xs text-muted-foreground">{prompt}</p> : null}
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label={prompt ?? "Suggested follow-ups"}
        >
          {suggestions.map((s, i) => {
            const Icon = iconForKind(s.kind);
            const tier = confidenceTier(s.confidence) ?? "none";
            return (
              <button
                key={`${s.kind ?? "s"}-${s.label}-${i}`}
                type="button"
                disabled={disabled}
                onClick={() => onSelect?.(s)}
                style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                className={chipVariants({ tier })}
                title={s.kind ? `${s.kind} follow-up` : undefined}
              >
                <Icon className="size-3.5 shrink-0 opacity-80" aria-hidden />
                <span className="truncate">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  },
);

EnrichedSuggestion.displayName = "EnrichedSuggestion";

export {
  enrichedSuggestionVariants,
  chipVariants as enrichedSuggestionChipVariants,
  confidenceTier,
};
export type EnrichedSuggestionVariantProps = VariantProps<typeof enrichedSuggestionVariants>;
